import type {
  Camera, Catalogue, CompleteResult, Cost, Employee, FinanceSummary, Lease, Payment,
  Billing, Building, Invitation, Locations, Presigned, Property, RentCycle, Room, Session, Task, TaskDetail, Tenant,
} from './types';

/**
 * Every endpoint this app depends on, in one place.
 *
 * `PATHS` is exported so `scripts/doctor.mjs` can verify each one against a
 * running API. This app hand-writes its types rather than importing them from
 * the platform, so nothing else catches a renamed route: drift would show up as
 * a screen that silently displays nothing.
 */
export const PATHS = {
  login: 'POST /v1/auth/staff/login',
  refresh: 'POST /v1/auth/refresh',
  logout: 'POST /v1/auth/logout',
  forgot: 'POST /v1/auth/staff/forgot',
  resetPassword: 'POST /v1/auth/staff/reset',
  acceptInvite: 'POST /v1/employees/accept-invite',
  me: 'GET /v1/me',
  workflows: 'GET /v1/workflows',
  tasks: 'GET /v1/tasks',
  task: 'GET /v1/tasks/{id}',
  createTask: 'POST /v1/tasks',
  completeTask: 'POST /v1/tasks/{id}/complete',
  updateTask: 'PATCH /v1/tasks/{id}',
  deleteTask: 'DELETE /v1/tasks/{id}',
  properties: 'GET /v1/properties',
  createProperty: 'POST /v1/properties',
  updateProperty: 'PATCH /v1/properties/{id}',
  locations: 'GET /v1/locations',
  buildings: 'GET /v1/buildings',
  createBuilding: 'POST /v1/buildings',
  updateBuilding: 'PATCH /v1/buildings/{id}',
  billing: 'GET /v1/billing',
  rooms: 'GET /v1/properties/{id}/rooms',
  createRoom: 'POST /v1/properties/{id}/rooms',
  updateRoom: 'PATCH /v1/rooms/{id}',
  deleteRoom: 'DELETE /v1/rooms/{id}',
  cameras: 'GET /v1/cameras',
  createCamera: 'POST /v1/cameras',
  deleteCamera: 'DELETE /v1/cameras/{id}',
  leases: 'GET /v1/leases',
  rentCycle: 'GET /v1/leases/rent-cycle',
  moveOut: 'POST /v1/leases/{id}/move-out',
  financeSummary: 'GET /v1/finance/summary',
  payments: 'GET /v1/finance/payments',
  costs: 'GET /v1/finance/costs',
  addPayment: 'POST /v1/finance/payments',
  addCost: 'POST /v1/finance/costs',
  employees: 'GET /v1/employees',
  invite: 'POST /v1/employees/invite',
  invitations: 'GET /v1/employees/invitations',
  resendInvitation: 'POST /v1/employees/invitations/{id}/resend',
  withdrawInvitation: 'DELETE /v1/employees/invitations/{id}',
  updateEmployee: 'PATCH /v1/employees/{id}',
  removeEmployee: 'DELETE /v1/employees/{id}',
  tenants: 'GET /v1/tenants',
  createTenant: 'POST /v1/tenants',
  updateTenant: 'PATCH /v1/tenants/{id}',
  deleteTenant: 'DELETE /v1/tenants/{id}',
  presign: 'POST /v1/receipts/presign',
  confirmReceipt: 'POST /v1/receipts/{id}/confirm',
  receipt: 'GET /v1/receipts/{id}',
} as const;

const BASE = import.meta.env.VITE_API_URL ?? '';

export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly code?: string) {
    super(message);
    this.name = 'ApiError';
  }
}

let token: string | null = null;
export const setToken = (t: string | null): void => { token = t; };

/*
 * The access token lasts fifteen minutes; the refresh token buys another, once.
 * Without this the console signed everyone out four times an hour.
 */
let refreshToken: string | null = null;
let onRefreshed: ((fresh: { accessToken: string; refreshToken: string }) => void) | null = null;
/** This session's refresh token, and who to tell when it is spent and replaced. */
export const setRefresh = (
  r: string | null, cb: ((fresh: { accessToken: string; refreshToken: string }) => void) | null,
): void => { refreshToken = r; onRefreshed = cb; };

type Refreshed = 'ok' | 'expired' | 'unavailable';
let inflight: Promise<Refreshed> | null = null;
/**
 * One refresh at a time. Requests that fail together share it: presenting a
 * spent refresh token again looks like theft, and ends the session.
 *
 * Only the server refusing the token ends the session. A dropped connection or
 * the auth rate limit (429, shared by an office behind one address) leaves the
 * refresh token valid, so signing out then would throw away a working session.
 */
function refreshOnce(): Promise<Refreshed> {
  if (!refreshToken) return Promise.resolve('expired');
  inflight ??= (async () => {
    try {
      const res = await fetch(BASE + '/v1/auth/refresh', {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) return res.status === 401 || res.status === 403 ? 'expired' : 'unavailable';
      const fresh = (await res.json()) as { accessToken: string; refreshToken: string };
      token = fresh.accessToken;
      refreshToken = fresh.refreshToken;
      onRefreshed?.(fresh);
      return 'ok';
    } catch {
      return 'unavailable';
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

async function request<T>(method: string, path: string, body?: unknown, idempotencyKey?: string, retried = false): Promise<T> {
  let res: Response;
  try {
    res = await fetch(BASE + path, {
      method,
      headers: {
        'content-type': 'application/json',
        'x-burj-surface': 'ops-console',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(idempotencyKey ? { 'idempotency-key': idempotencyKey } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError('Cannot reach the API', 0, 'offline');
  }
  // on the auth routes a 401 is a wrong password or a spent link, not the end of a session
  const authRoute = path.startsWith('/v1/auth/');
  if (res.status === 401 && !retried && !authRoute) {
    const refreshed = await refreshOnce();
    if (refreshed === 'ok') return request<T>(method, path, body, idempotencyKey, true);
    if (refreshed === 'unavailable') {
      throw new ApiError('Could not renew the session. Check the connection and try again.', 0, 'refresh_unavailable');
    }
  }
  if (res.status === 401 && !authRoute) window.dispatchEvent(new Event('burj:signed-out'));
  if (res.status === 204) return undefined as T;
  const payload = await res.json().catch(() => ({}));
  if (!res.ok) {
    const e = payload as { error?: { message?: string; code?: string } };
    throw new ApiError(e.error?.message ?? `HTTP ${res.status}`, res.status, e.error?.code);
  }
  return payload as T;
}

/** Generated when the operator clicks, never at send time, so a retry replays. */
export const newKey = (): string =>
  crypto.randomUUID?.() ?? `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;

type Body = Record<string, unknown>;

export const api = {
  login: (email: string, password: string) => request<Session>('POST', '/v1/auth/staff/login', { email, password }),
  /** Ends the session's refresh family on the server, so a copied token dies too. Best effort. */
  logout: (refreshToken: string) =>
    request<void>('POST', '/v1/auth/logout', { refreshToken }).catch(() => undefined),
  forgot: (email: string) => request<{ sent: true }>('POST', '/v1/auth/staff/forgot', { email }),
  /** From the emailed reset link. Every other session of that person ends. */
  resetPassword: (token: string, password: string) =>
    request<{ reset: true }>('POST', '/v1/auth/staff/reset', { token, password }),
  /** From the emailed invitation. Answers like a sign-in. */
  acceptInvite: (token: string, password: string) =>
    request<Session>('POST', '/v1/employees/accept-invite', { token, password }),

  /** The catalogue. Everything this console renders about workflows comes from here. */
  workflows: () => request<Catalogue>('GET', '/v1/workflows'),

  tasks: (q: { status?: string; propertyId?: string; workflow?: string; limit?: number } = {}) => {
    const p = new URLSearchParams();
    for (const [k, v] of Object.entries(q)) if (v !== undefined) p.set(k, String(v));
    return request<{ tasks: Task[] }>('GET', `/v1/tasks?${p.toString()}`);
  },
  task: (id: string) => request<TaskDetail>('GET', `/v1/tasks/${id}`),
  createTask: (b: Body) => request<{ task: Task }>('POST', '/v1/tasks', b),
  completeTask: (id: string, b: Body, key: string) =>
    request<CompleteResult>('POST', `/v1/tasks/${id}/complete`, b, key),
  /** Pending ↔ In progress, assignee, dates, money, notes. Never Done: that is completeTask. */
  updateTask: (id: string, b: Body) => request<{ task: Task }>('PATCH', `/v1/tasks/${id}`, b),
  reassign: (id: string, assigneeId: string) =>
    request<{ task: Task }>('PATCH', `/v1/tasks/${id}`, { assigneeId }),
  deleteTask: (id: string) => request<void>('DELETE', `/v1/tasks/${id}`),

  properties: () => request<{ properties: Property[] }>('GET', '/v1/properties'),
  createProperty: (b: Body) => request<{ property: Property }>('POST', '/v1/properties', b),
  updateProperty: (id: string, b: Body) => request<{ property: Property }>('PATCH', `/v1/properties/${id}`, b),
  /** The countries, cities and districts staff choose from. */
  locations: () => request<Locations>('GET', '/v1/locations'),
  buildings: () => request<{ buildings: Building[] }>('GET', '/v1/buildings'),
  createBuilding: (b: Body) => request<{ building: Building }>('POST', '/v1/buildings', b),
  /** Moving a building moves every property in it. */
  updateBuilding: (id: string, b: Body) => request<{ building: Building }>('PATCH', `/v1/buildings/${id}`, b),
  /** Readable even while the account is suspended: what is owed, and where to pay it. */
  billing: () => request<Billing>('GET', '/v1/billing'),
  rooms: (propertyId: string) => request<{ rooms: Room[] }>('GET', `/v1/properties/${propertyId}/rooms`),
  createRoom: (propertyId: string, b: Body) =>
    request<{ room: Room }>('POST', `/v1/properties/${propertyId}/rooms`, b),
  /** Number and asking range only. Occupancy and rent belong to the lease. */
  updateRoom: (id: string, b: Body) => request<{ room: Room }>('PATCH', `/v1/rooms/${id}`, b),
  deleteRoom: (id: string) => request<void>('DELETE', `/v1/rooms/${id}`),

  cameras: () => request<{ cameras: Camera[] }>('GET', '/v1/cameras'),
  createCamera: (b: { propertyId: string; location: string; streamUrl: string }) =>
    request<{ camera: Camera }>('POST', '/v1/cameras', b),
  deleteCamera: (id: string) => request<void>('DELETE', `/v1/cameras/${id}`),

  leases: () => request<{ leases: Lease[] }>('GET', '/v1/leases'),
  rentCycle: () => request<RentCycle>('GET', '/v1/leases/rent-cycle'),
  moveOut: (leaseId: string) => request<{ taskId: string }>('POST', `/v1/leases/${leaseId}/move-out`),

  financeSummary: () => request<FinanceSummary>('GET', '/v1/finance/summary'),
  payments: () => request<{ payments: Payment[] }>('GET', '/v1/finance/payments'),
  costs: () => request<{ costs: Cost[] }>('GET', '/v1/finance/costs'),
  addPayment: (b: Body) => request<{ payment: Payment }>('POST', '/v1/finance/payments', b),
  addCost: (b: Body) => request<{ cost: Cost }>('POST', '/v1/finance/costs', b),

  employees: () => request<{ employees: Employee[] }>('GET', '/v1/employees'),
  /** Not yet accepted: each holds a seat. */
  invitations: () => request<{ invitations: Invitation[] }>('GET', '/v1/employees/invitations'),
  resendInvitation: (id: string) => request<{ expiresInHours: number }>('POST', `/v1/employees/invitations/${id}/resend`),
  withdrawInvitation: (id: string) => request<void>('DELETE', `/v1/employees/invitations/${id}`),
  invite: (b: { name: string; email: string; role: string }) =>
    request<{ invitationId: string; expiresInHours: number }>('POST', '/v1/employees/invite', b),
  updateEmployee: (id: string, b: Body) => request<{ employee: Employee }>('PATCH', `/v1/employees/${id}`, b),
  /** Deactivates — the person's history stays attributed to them. */
  removeEmployee: (id: string) => request<void>('DELETE', `/v1/employees/${id}`),

  tenants: () => request<{ tenants: Tenant[] }>('GET', '/v1/tenants'),
  createTenant: (b: Body) => request<{ tenant: Tenant }>('POST', '/v1/tenants', b),
  updateTenant: (id: string, b: Body) => request<{ tenant: Tenant }>('PATCH', `/v1/tenants/${id}`, b),
  deleteTenant: (id: string) => request<void>('DELETE', `/v1/tenants/${id}`),

  presign: (contentType: string, byteSize: number) =>
    request<Presigned>('POST', '/v1/receipts/presign', { contentType, byteSize }),
  confirmReceipt: (id: string) => request<{ status: string }>('POST', `/v1/receipts/${id}/confirm`, {}),
  /** A five-minute link to the file itself. Open it straight away; do not store it. */
  receipt: (id: string) =>
    request<{ receipt: { id: string; contentType: string; byteSize: number }; url: string }>('GET', `/v1/receipts/${id}`),
};

/** What object storage accepts. iPhone photos arrive as HEIC and are refused at presign. */
export const RECEIPT_TYPES = 'image/jpeg,image/png,application/pdf';
const RECEIPT_MAX_BYTES = 10 * 1024 * 1024;

/** Checked here so the operator hears why, rather than a bare 400 from presign. */
function receiptProblem(file: File): void {
  if (!RECEIPT_TYPES.split(',').includes(file.type)) {
    throw new ApiError('Use a JPEG or PNG photo, or a PDF. Other formats (HEIC, WebP) are not accepted.', 0, 'bad_type');
  }
  if (file.size > RECEIPT_MAX_BYTES) {
    throw new ApiError('That file is larger than 10 MB. Use a smaller photo or scan.', 0, 'too_large');
  }
}

/**
 * Receipt bytes go straight to object storage, never through the API — which is
 * why the API body limit is 2 MB. A task cannot close until the upload is
 * confirmed, so this must succeed before a money task can be completed.
 */
export async function uploadReceipt(file: File): Promise<string> {
  receiptProblem(file);
  const pre = await api.presign(file.type, file.size);
  const put = await fetch(pre.uploadUrl, { method: 'PUT', headers: pre.headers, body: file });
  if (!put.ok) throw new ApiError(`Upload failed (${put.status})`, put.status);
  await api.confirmReceipt(pre.receiptId);
  return pre.receiptId;
}

/**
 * Opens a stored receipt or photo in a new tab.
 *
 * The tab is opened before the request so a popup blocker treats it as the
 * click's own; the short-lived URL is filled in when it arrives.
 */
export async function openReceipt(id: string): Promise<void> {
  const tab = window.open('about:blank', '_blank');
  try {
    const { url } = await api.receipt(id);
    if (tab) tab.location.href = url; else window.location.assign(url);
  } catch (e) {
    tab?.close();
    throw e;
  }
}
